import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';

import SideBarComponent from './AgentComponent/SideBarComponent';
import HeaderComponent from './AgentComponent/HeaderComponent';
import TabComponent from './AgentComponent/TabComponent';
import ContentPanel from './AgentComponent/ContentPannel';
import RightPanel from './AgentComponent/RightPannel';
import AddAgentModal from './child/AddAgentModal';

import { fetchAssistants, fetchAssistantDetail } from '../store/app/assistantSlice';
import { User } from 'lucide-react';

const TABS = ['Agent', 'LLM', 'Transcriber', 'Voice', 'Summary', 'Evaluation', 'StructureData'];

export default function AgentLayer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: urlAssistantId } = useParams();

  const assistants = useSelector(state => state.assistants.value);
  const assistantDetail = useSelector(state => state.assistants.activeDetail);
  const userOrgId = useSelector(state => state?.user?.user?.organizationId);

  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [activeAssistantId, setActiveAssistantId] = useState(null);
  const [showAddAgentModal, setShowAddAgentModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  // Fetch assistants list
  useEffect(() => {
    if (userOrgId) {
      const loadData = async () => {
        setIsLoading(true);
        await dispatch(fetchAssistants(userOrgId));
        setIsLoading(false);
        setHasFetched(true);
      };
      loadData();
    }
  }, [dispatch, userOrgId]);

  // Set activeAssistantId from URL
  useEffect(() => {
    if (urlAssistantId) {
      setActiveAssistantId(urlAssistantId);
    }
  }, [urlAssistantId]);

  // If no ID in URL and we have assistants, set default and navigate
  useEffect(() => {
    if (!urlAssistantId && assistants.length > 0) {
      const defaultId = assistants[0].id;
      setActiveAssistantId(defaultId);
      navigate(`/assistant/${defaultId}`, { replace: true });
    }
  }, [assistants, urlAssistantId, navigate]);

  // If ID from URL is invalid, fallback to first available assistant
  useEffect(() => {
    if (
      urlAssistantId &&
      assistants.length > 0 &&
      !assistants.find(a => a.id === urlAssistantId)
    ) {
      const fallbackId = assistants[0].id;
      setActiveAssistantId(fallbackId);
      navigate(`/assistant/${fallbackId}`, { replace: true });
    }
  }, [assistants, urlAssistantId, navigate]);

  // Fetch assistant details when ID changes
  useEffect(() => {
    const fetchData = async () => {
      if (activeAssistantId) {
        setIsLoading(true);
        await dispatch(fetchAssistantDetail(activeAssistantId));
        setIsLoading(false);
      }
    };
    fetchData();
  }, [activeAssistantId, dispatch]);

  const handleSelectAssistant = useCallback((id) => {
    setActiveAssistantId(id);
    navigate(`/assistant/${id}`);
  }, [navigate]);

  const handleSetActiveTab = () => setActiveTab(TABS[0]);

  const costData = useMemo(() => ([
    { label: 'Transcriber', value: '$0.006', color: '#4caf50' },
    { label: 'LLM', value: '$0.003', color: '#2196f3' },
    { label: 'Voice', value: '$0.030', color: '#ff9800' },
    { label: 'Platform', value: '$0.020', color: '#ffc107' },
  ]), []);

  const activeAssistant = useMemo(
    () => assistants.find(a => a.id === activeAssistantId)?.name || '',
    [assistants, activeAssistantId]
  );

  // === Empty State ===
  if (hasFetched && assistants.length === 0) {
    return (
      <motion.div
        className="agent-layer-container flex"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        style={{ height: '80vh', alignItems: 'center', justifyContent: 'center' }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '32px 16px',
          color: '#4B5563',
          fontSize: '14px',
          width: '100%',
          maxWidth: '600px'
        }}>
          <div style={{
            width: '72px',
            height: '72px',
            border: '2px dotted #d1d5db',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px'
          }}>
            <User size={40} color="#9ca3af" />
          </div>
          <div style={{ textAlign: 'left', width: '100%' }}>
            <h5 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '8px'
            }}>Assistants</h5>
            <p style={{
              color: '#6B7280',
              marginBottom: '16px',
              lineHeight: '1.5'
            }}>
              Assistants are voice AI chat bots used for integrations into your applications.<br />
              You can fully configure them to your business's needs, and we support all major models and providers.
            </p>
            <button
              className="btn btn-primary"
              style={{ padding: '0.4rem 0.75rem' }}
              onClick={() => setShowAddAgentModal(true)}
            >
              + Create Assistant
            </button>
          </div>
        </div>

        {showAddAgentModal && (
          <AddAgentModal handleCancel={() => setShowAddAgentModal(false)} />
        )}
      </motion.div>
    );
  }

  // === Main Assistant UI ===
  return (
    <motion.div
      className="agent-layer-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <div>
        <SideBarComponent
          assistants={assistants}
          activeAssistantId={activeAssistantId}
          onSelectAssistant={handleSelectAssistant}
          handleSetActiveTab={handleSetActiveTab}
        />
      </div>

      <main className="agent-main col">
        <HeaderComponent />
        <TabComponent
          tabs={TABS}
          activeTab={activeTab}
          onTabClick={setActiveTab}
        />
        <section className="agent-content">
          <ContentPanel
            activeTab={activeTab}
            assistantDetail={assistantDetail?.assistant}
          />
          <RightPanel assistantId={activeAssistantId} />
        </section>
      </main>

      {showAddAgentModal && (
        <AddAgentModal handleCancel={() => setShowAddAgentModal(false)} />
      )}

      {isLoading && (
        <div
          className="flex"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              border: '6px solid #d4f8d4',
              borderTop: '6px solid #28a745',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              animation: 'greenSpin 1s linear infinite',
            }}
          />
        </div>
      )}
    </motion.div>
  );
}
